import {v4 as uuidv4} from "uuid";
import {none, option} from "ts-option";
import BaseFormController from "../../../validation/BaseFormController";
import {FormField, OptionalFormField, RequiredFormField} from "../../../validation/FormField";
import FormValidators from "../../../validation/FormValidators";
import FormValidation from "../../../validation/FormValidation";
import FinanceProduct from "../../../model/financeProduct/FinanceProduct";
import {isDefined} from "../../../utils/Utils";

export default class FinanceProductForm extends BaseFormController<FinanceProductForm> {

	constructor(public readonly formId: string,
		public readonly label: RequiredFormField<string>,
		public readonly description: OptionalFormField<string>,
		public readonly provider: RequiredFormField<string>,
		public readonly annualPercentageRate: RequiredFormField<number>,
		public readonly fee: RequiredFormField<boolean>,
		public readonly feePercentage: OptionalFormField<number>,
		public readonly disabled: RequiredFormField<boolean>) {
		super(label, formId);
	}

	public static createEmpty(): FinanceProductForm {
		return this.createFinanceProductForm({disabled: option(false), fee: option(false)});
	}

	public static createFromFinanceProduct(product: FinanceProduct): FinanceProductForm {
		return this.createFinanceProductForm({
			label: option(product.label),
			description: product.description,
			provider: option(product.provider),
			annualPercentageRate: option(product.annualPercentageRate),
			fee: option(product.fee.isDefined),
			feePercentage: product.fee,
			disabled: option(product.disabled)
		});
	}

	private static createFinanceProductForm({
		label = none,
		description = none,
		provider = none,
		annualPercentageRate = none,
		fee = none,
		feePercentage = none,
		disabled = none
	}) {
		const feeField = RequiredFormField.create<boolean>(fee, "Fee is required");
		return new FinanceProductForm(
			uuidv4(),
			RequiredFormField.create<string>(label, "Label is required", [
				FormValidators.nonEmptyString("Label is required")
			]),
			OptionalFormField.create<string>(description),
			RequiredFormField.create<string>(provider, "Provider is required", [
				FormValidators.nonEmptyString("Provider is required"),
			]),
			RequiredFormField.create<number>(annualPercentageRate, "APR is required"),
			feeField,
			OptionalFormField.create<number>(feePercentage, [isFeePercentageRequired("Fee percentage is required", feeField.id)]),
			RequiredFormField.create<boolean>(disabled, "Disabled is required")
		);
	}

	onValid(callback: (data: FinanceProduct) => void) {
		const result = FormValidation.validateFields(
			[this.label, this.provider, this.annualPercentageRate, this.fee, this.feePercentage, this.disabled],
			() => {
				return new FinanceProduct(
					uuidv4(),
					this.label.getValue(),
					this.description.getOptionalValue(),
					this.provider.getValue(),
					this.annualPercentageRate.getValue(),
					this.feePercentage.getOptionalValue(),
					this.disabled.getValue(),
				)
			}
		);
		result.onSuccess(callback);
	}

	copy({
		label = this.label,
		description = this.description,
		provider = this.provider,
		annualPercentageRate = this.annualPercentageRate,
		fee = this.fee,
		feePercentage = this.feePercentage,
		disabled = this.disabled
	}): FinanceProductForm {
		return new FinanceProductForm(this.formId,
			label,
			description,
			provider,
			annualPercentageRate,
			fee,
			feePercentage,
			disabled);
	}

	fields(): Array<FormField<any>> {
		return [];
	}
}

const isFeePercentageRequired = (errorMsg: string, feeFieldId: string) => {
	return (form: BaseFormController<FinanceProductForm>, thisValue: number): string[] => {
		const otherFormField: RequiredFormField<boolean> = form.findFieldById<RequiredFormField<boolean>>(feeFieldId).get;
		if (otherFormField.getValue() && !isDefined(thisValue))
			return [errorMsg];
		else
			return [];
	}
};